import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { GitBranch, Calendar, RefreshCw, ChevronRight, FileDown, FileText, Download, Eye, Video } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import WorkProcessFlowchart from "@/components/workprocesses/WorkProcessFlowchart";
import { exportWorkProcessToPDF } from "@/lib/pdfExport";
import { TrainingVideosSection } from "@/components/training/TrainingVideosSection";

interface Department {
  id: string;
  name: string;
}

interface WorkProcess {
  id: string;
  department_id: string;
  title: string;
  description: string | null;
  flowchart_data: { nodes: any[]; edges: any[] };
  created_at: string;
  updated_at: string;
  renewed_at: string | null;
  is_active: boolean;
  document_path?: string | null;
  document_name?: string | null;
  document_mime_type?: string | null;
  document_size?: number | null;
}

const WorkProcessesPage = () => {
  const { userRole, profile } = useAuth();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [processes, setProcesses] = useState<WorkProcess[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedProcess, setSelectedProcess] = useState<WorkProcess | null>(null);
  const [userDepartmentId, setUserDepartmentId] = useState<string | null>(null);

  const isAdmin = userRole === 'admin' || userRole === 'hr_manager';

  useEffect(() => {
    loadDepartments();
    loadUserDepartment();
  }, []);

  useEffect(() => {
    if (departments.length > 0) {
      loadProcesses();
    }
  }, [departments, userDepartmentId, isAdmin]);

  const loadUserDepartment = async () => {
    if (!profile?.employee_id) return;
    
    try {
      const { data, error } = await supabase
        .from('employees')
        .select('department')
        .eq('id', profile.employee_id)
        .single();

      if (error) throw error;
      
      // Find matching department ID by name
      if (data?.department) {
        const matchingDept = departments.find(d => d.name === data.department);
        if (matchingDept) {
          setUserDepartmentId(matchingDept.id);
        }
      }
    } catch (error) {
      console.error('Error loading user department:', error);
    }
  };

  const loadDepartments = async () => {
    try {
      const { data, error } = await supabase
        .from('departments')
        .select('id, name')
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setDepartments(data || []);
    } catch (error) {
      console.error('Error loading departments:', error);
      toast.error("Fehler beim Laden der Abteilungen");
    }
  };

  const loadProcesses = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('work_processes')
        .select('*')
        .eq('is_active', true)
        .not('flowchart_data', 'is', null)
        .order('sort_order', { ascending: true });

      // Non-admins only see their department's processes
      if (!isAdmin && userDepartmentId) {
        query = query.eq('department_id', userDepartmentId);
      }

      const { data, error } = await query;

      if (error) throw error;
      
      // Parse flowchart_data for each process
      const parsedData = (data || []).map(p => ({
        ...p,
        flowchart_data: typeof p.flowchart_data === 'string' 
          ? JSON.parse(p.flowchart_data) 
          : p.flowchart_data || { nodes: [], edges: [] }
      }));
      
      setProcesses(parsedData);
    } catch (error) {
      console.error('Error loading processes:', error);
      toast.error("Fehler beim Laden der Prozesse");
    } finally {
      setLoading(false);
    }
  };

  const getDepartmentName = (departmentId: string) => {
    return departments.find(d => d.id === departmentId)?.name || 'Unbekannt';
  };

  const handleOpenProcess = (process: WorkProcess) => {
    setSelectedProcess(process);
    setViewerOpen(true);
  };

  const handleExportPDF = async (process: WorkProcess, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await exportWorkProcessToPDF({
        title: process.title,
        description: process.description,
        departmentName: getDepartmentName(process.department_id),
        flowchart_data: process.flowchart_data,
        createdAt: format(new Date(process.created_at), 'dd.MM.yyyy', { locale: de }),
        renewedAt: process.renewed_at 
          ? format(new Date(process.renewed_at), 'dd.MM.yyyy', { locale: de })
          : null,
      });
      toast.success("PDF wurde erfolgreich exportiert");
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error("Fehler beim PDF-Export");
    }
  };

  const handlePreviewDocument = async (process: WorkProcess, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!process.document_path) return;
    
    try {
      const { data, error } = await supabase.storage
        .from('work-process-documents')
        .download(process.document_path);
      
      if (error) throw error;
      
      const blobUrl = URL.createObjectURL(data);
      window.open(blobUrl, '_blank');
    } catch (error) {
      console.error('Error previewing document:', error);
      toast.error("Fehler beim Öffnen des Dokuments");
    }
  };

  const handleDownloadDocument = async (process: WorkProcess, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!process.document_path || !process.document_name) return;
    
    try {
      const { data, error } = await supabase.storage
        .from('work-process-documents')
        .download(process.document_path);
      
      if (error) throw error;
      
      const blobUrl = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = process.document_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
      
      toast.success("Dokument heruntergeladen");
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error("Fehler beim Herunterladen des Dokuments");
    }
  };

  // Group processes by department
  const groupedProcesses = processes.reduce((acc, process) => {
    const deptId = process.department_id;
    if (!acc[deptId]) {
      acc[deptId] = [];
    }
    acc[deptId].push(process);
    return acc;
  }, {} as Record<string, WorkProcess[]>);

  return (
    <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8">
      <header className="mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Arbeitsprozesse & Lernvideos</h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            {isAdmin 
              ? "Arbeitsabläufe, Prozessdokumentation und Schulungsvideos"
              : "Arbeitsabläufe und Schulungsvideos für Ihre Abteilung"}
          </p>
        </div>
      </header>

      <Tabs defaultValue="processes" className="space-y-6">
        <TabsList>
          <TabsTrigger value="processes" className="gap-2">
            <GitBranch className="h-4 w-4" />
            Arbeitsprozesse
          </TabsTrigger>
          <TabsTrigger value="videos" className="gap-2">
            <Video className="h-4 w-4" />
            Lernvideos
          </TabsTrigger>
        </TabsList>

        <TabsContent value="videos">
          <TrainingVideosSection departmentId={userDepartmentId || undefined} />
        </TabsContent>

        <TabsContent value="processes">
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">Wird geladen...</div>
        </div>
      ) : processes.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <GitBranch className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">Keine Prozesse verfügbar</h3>
            <p className="text-muted-foreground text-center">
              {isAdmin 
                ? "Erstellen Sie Arbeitsprozesse unter Einstellungen → Arbeitsprozesse."
                : "Für Ihre Abteilung wurden noch keine Arbeitsprozesse freigegeben."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {Object.entries(groupedProcesses).map(([deptId, deptProcesses]) => (
            <section key={deptId}>
              <div className="flex items-center gap-2 mb-4">
                <Badge variant="outline" className="text-sm font-medium px-3 py-1">
                  {getDepartmentName(deptId)}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  {deptProcesses.length} {deptProcesses.length === 1 ? 'Prozess' : 'Prozesse'}
                </span>
              </div>
              
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {deptProcesses.map((process) => (
                  <Card 
                    key={process.id} 
                    className="group cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => handleOpenProcess(process)}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-lg group-hover:text-primary transition-colors">
                          {process.title}
                        </CardTitle>
                        <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                      </div>
                      {process.description && (
                        <CardDescription className="line-clamp-2">
                          {process.description}
                        </CardDescription>
                      )}
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          <span>Erstellt: {format(new Date(process.created_at), 'dd.MM.yyyy', { locale: de })}</span>
                        </div>
                        {process.renewed_at && (
                          <div className="flex items-center gap-1">
                            <RefreshCw className="h-3.5 w-3.5" />
                            <span>Erneuert: {format(new Date(process.renewed_at), 'dd.MM.yyyy', { locale: de })}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Document Attachment */}
                      {process.document_name && (
                        <div className="mt-3 pt-3 border-t border-border">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm truncate flex-1">{process.document_name}</span>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => handlePreviewDocument(process, e)}
                              title="Vorschau"
                              className="h-7 w-7"
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => handleDownloadDocument(process, e)}
                              title="Herunterladen"
                              className="h-7 w-7"
                            >
                              <Download className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

        </TabsContent>
      </Tabs>

      {/* Process Viewer Dialog */}
      <Dialog open={viewerOpen} onOpenChange={setViewerOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto flex flex-col">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <GitBranch className="h-5 w-5" />
              {selectedProcess?.title}
            </DialogTitle>
            {selectedProcess && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleExportPDF(selectedProcess, { stopPropagation: () => {} } as React.MouseEvent)}
                className="gap-2"
              >
                <FileDown className="h-4 w-4" />
                PDF Export
              </Button>
            )}
          </DialogHeader>
          <div className="flex-1 overflow-auto">
            {selectedProcess && (
              <WorkProcessFlowchart 
                flowchartData={selectedProcess.flowchart_data}
                readOnly
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkProcessesPage;